import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { createElement, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbDotsVertical, TbTrash } from "react-icons/tb";

import { hotelClinicStatusItems } from "@/constants/hotelClinicStatuses";
import { HotelClinicStatus } from "@/models/hotel-clinic.model";
import {
    ClinicArrivalTasksBulkConfirmTypes,
    useClinicArrivalTasksGridActions,
    useClinicArrivalTasksGridSelectedRows,
} from "@/stores/use-clinic-arrival-tasks-grid";

import { HotelClinicTaskResolved } from "../../types";

const emptyStatusSets = (): Record<HotelClinicStatus, Set<string>> => ({
    [HotelClinicStatus.PENDING]: new Set<string>(),
    [HotelClinicStatus.IN_TRANSIT]: new Set<string>(),
    [HotelClinicStatus.DONE]: new Set<string>(),
});

function ClinicArrivalTasksActionsHeader({ allRows }: { allRows: HotelClinicTaskResolved[] }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const selectedRowIds = useClinicArrivalTasksGridSelectedRows();
    const { updateBulkTasksConfirm } = useClinicArrivalTasksGridActions();
    const { t } = useTranslation();

    const selectedRows = useMemo(
        () => allRows.filter((row) => selectedRowIds.includes(row.id)),
        [allRows, selectedRowIds]
    );

    const infos: Record<HotelClinicStatus, Set<string>> = useMemo(() => {
        return selectedRows.reduce((prev, row) => {
            hotelClinicStatusItems.forEach((item) => {
                if (row.status !== item.status) {
                    prev[item.status].add(row.id);
                }
            });
            return prev;
        }, emptyStatusSets());
    }, [selectedRows]);

    const handleBulkToStatus = useCallback(
        (targetStatus: HotelClinicStatus) => {
            const ids = infos[targetStatus];
            if (!ids?.size) return;
            updateBulkTasksConfirm({
                type: ClinicArrivalTasksBulkConfirmTypes.STATUS,
                tasks: [...ids].map((taskId) => ({
                    taskId,
                    updateData: { status: targetStatus },
                })),
            });
        },
        [infos, updateBulkTasksConfirm]
    );

    const handleBulkDeleteSelected = useCallback(() => {
        if (!selectedRowIds.length) return;
        updateBulkTasksConfirm({
            type: ClinicArrivalTasksBulkConfirmTypes.DELETE,
            tasks: selectedRowIds.map((taskId) => ({
                taskId: String(taskId),
                updateData: { deleteTask: true },
            })),
        });
    }, [selectedRowIds, updateBulkTasksConfirm]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <div className="flex items-center justify-center !mx-auto">
            {hotelClinicStatusItems.length > 0 && (
                <>
                    <IconButton onClick={handleClick} size="small" className=" !w-[30px] grow-0 shrink-0 !h-[30px]">
                        <TbDotsVertical size={18} />
                    </IconButton>
                    <Menu
                        sx={{
                            ":root": {},
                        }}
                        classes={{
                            paper: "!min-w-[178.58px]",
                            list: "!min-w-[178.58px]",
                        }}
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                    >
                        {hotelClinicStatusItems.map((item) => (
                            <MenuItem
                                disabled={!infos[item.status]?.size}
                                key={item.status}
                                onClick={() => handleBulkToStatus(item.status)}
                                sx={{}}
                            >
                                <ListItemIcon>{createElement(item.component(), { size: 18 })}</ListItemIcon>
                                <ListItemText>{t(item.updateActionLabel)}</ListItemText>
                                <Typography variant="body2" sx={{ color: "text.secondary", paddingLeft: 3 }}>
                                    {infos[item.status]?.size ? infos[item.status]?.size.toString() : "0"}
                                </Typography>
                            </MenuItem>
                        ))}
                        <Divider />
                        <MenuItem disabled={!selectedRowIds.length} onClick={handleBulkDeleteSelected}>
                            <ListItemIcon>
                                <TbTrash size={18} />
                            </ListItemIcon>
                            <ListItemText>{t("clinicArrival.deleteTasksMenu")}</ListItemText>
                            <Typography variant="body2" sx={{ color: "text.secondary", paddingLeft: 3 }}>
                                {selectedRowIds.length ? selectedRowIds.length.toString() : "0"}
                            </Typography>
                        </MenuItem>
                    </Menu>
                </>
            )}
        </div>
    );
}

export default ClinicArrivalTasksActionsHeader;
